import React, { useMemo, useState } from 'react'
import { Box, Button, MenuItem, Paper, Select, TextField, Tooltip, Typography } from '@mui/material'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import LayersIcon from '@mui/icons-material/Layers'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import PercentIcon from '@mui/icons-material/Percent'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import DescriptionIcon from '@mui/icons-material/Description'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import SendIcon from '@mui/icons-material/Send'
import HandshakeIcon from '@mui/icons-material/Handshake'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import CreateIcon from '@mui/icons-material/Create'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import OpacityIcon from '@mui/icons-material/Opacity'
import { useGetPipelineFunnelQuery } from '../../redux/growthosPipelineFunnelApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import {
  GX_BORDER, GX_INK_500, GX_INK_900, GX_COLORS, GX_CARD_SX, GX_PAGE_SX,
  GxKpiCard, GxPill, GxSyncStatusBar,
} from '../growthos-shared/GrowthOsUi'
import {
  Badge, DataTable, ExpandableTable, HubspotLink, MBR_TITLE, Muted, Neg, PfSection, ScopeTag, TaperedFunnel,
  compactCurrency, integer, percentage,
} from './pfUi'
import { DealTable } from './PfDealTable'
import PipelineLeadFlow from './PipelineLeadFlow'

const fmtDay = (iso) => (iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '')

const PERIODS = [
  { value: 'mtd', label: 'MTD' }, { value: 'last_month', label: 'Last Month' },
  { value: 'last_3m', label: 'Last 3M' }, { value: 'last_6m', label: 'Last 6M' },
  { value: 'ytd', label: 'YTD' }, { value: 'all', label: 'All time' }, { value: 'custom', label: 'Custom' },
]

// One icon + gradient per canonical rung; a future 8th rung degrades to a default.
const RUNG_ICON = [DescriptionIcon, LightbulbIcon, SendIcon, HandshakeIcon, FactCheckIcon, CreateIcon, RocketLaunchIcon]
const RUNG_GRADIENT = [
  'linear-gradient(135deg,#6366f1,#8b5cf6)', 'linear-gradient(135deg,#8b5cf6,#a855f7)',
  'linear-gradient(135deg,#0ea5e9,#06b6d4)', 'linear-gradient(135deg,#f59e0b,#f97316)',
  'linear-gradient(135deg,#14b8a6,#0d9488)', 'linear-gradient(135deg,#22c55e,#16a34a)',
  'linear-gradient(135deg,#059669,#047857)',
]

function Kpi({ definition, ...props }) {
  return (
    <Tooltip title={definition || ''} arrow disableHoverListener={!definition}>
      <Box sx={{ display: 'flex', minWidth: 0 }}><GxKpiCard {...props} /></Box>
    </Tooltip>
  )
}

function ToggleButton({ active, onClick, children }) {
  return (
    <Box component="button" onClick={onClick} sx={{
      px: 1.5, py: 0.75, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
      bgcolor: active ? GX_COLORS.indigo : 'transparent', color: active ? '#fff' : GX_INK_500,
    }}>{children}</Box>
  )
}

export default function PipelineFunnelPage() {
  const [period, setPeriod] = useState('mtd')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [selectedStep, setSelectedStep] = useState(null)
  const [matrixMode, setMatrixMode] = useState('deals')
  const customReady = period !== 'custom' || Boolean(customFrom && customTo)

  const { data, error, isFetching } = useGetPipelineFunnelQuery(
    { period, from: period === 'custom' ? customFrom : undefined, to: period === 'custom' ? customTo : undefined },
    { skip: !customReady },
  )
  const { data: runsData } = useListIngestRunsQuery()
  const range = data ? `${fmtDay(data.window.from)} – ${fmtDay(data.window.to)}` : ''
  const k = data?.kpis
  const hub = data?.hubspot
  const ladder = useMemo(() => data?.ladder || [], [data])

  const funnelStages = useMemo(() => (data?.steps || []).map((s, i) => ({
    key: String(s.step),
    label: s.label,
    count: s.deals,
    pct: s.pctOfEntryPct,
    icon: RUNG_ICON[i] || LayersIcon,
    gradient: RUNG_GRADIENT[i] || 'linear-gradient(135deg,#64748b,#475569)',
  })), [data])

  const toggleStep = (step) => setSelectedStep((cur) => (cur === step ? null : step))
  const activeStep = selectedStep == null ? null : (data?.byStep || []).find((s) => s.step === selectedStep) || null
  const activeStepMeta = selectedStep == null ? null : (data?.steps || []).find((s) => s.step === selectedStep) || null
  // Deals that reached the selected rung — the SAME predicate the backend used for the tree.
  const activeStepDeals = useMemo(() => {
    if (selectedStep == null) return []
    return (data?.cohortDeals || []).filter((d) => (d.deepestStep == null ? -1 : d.deepestStep) >= selectedStep)
  }, [data, selectedStep])

  return (
    <Box sx={GX_PAGE_SX}>
      <GxSyncStatusBar runs={runsData?.runs || []} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <AccountTreeIcon sx={{ fontSize: 15, color: GX_COLORS.indigo }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: GX_COLORS.indigo }}>Marketing · All Pipelines</Typography>
          </Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>Pipeline Funnel — All Products</Typography>
          <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.25, maxWidth: 820 }}>
            Every pipeline (D2C Freight, Warehousing, PTL, Packaging, Tech, FTL) on one canonical stage ladder — how far deals got, where they leaked, and who owns them.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select size="small" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ minWidth: 144, fontSize: 13, bgcolor: '#fff' }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
          {period === 'custom' && (
            <>
              <TextField size="small" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <TextField size="small" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </>
          )}
          {range && <GxPill color={GX_COLORS.indigo}>Showing: {range}</GxPill>}
        </Box>
      </Box>

      {/* Scope disclosure — marketing-sourced, Est. MBR currency, cohort basis, history coverage. */}
      {data && data.scope && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <Badge title={`Pipelines on the ladder: ${data.scope.pipelineCount}`}>{data.scope.pipelineCount} pipelines · marketing-sourced</Badge>
          <ScopeTag label="Value: Est. MBR (₹/mo)" title={data.scope.valueCurrencyNote} />
          <ScopeTag label="Funnel: created-in-period cohort" title="The funnel counts deals CREATED in the selected window and asks how far each one ever got (from stage history). The pipeline × stage matrix and the stalled-deal table below are LIVE snapshots of the current backlog instead, independent of the period." />
          <ScopeTag label="No leads axis" title={data.scope.leadsNote} />
          {k && k.stageHistoryCoveragePct != null && (
            <ScopeTag
              label={`Stage history: ${percentage(k.stageHistoryCoveragePct)}`}
              title={`${integer(k.dealsWithoutStageHistory)} deal(s) have no captured stage history and fall back to their current stage. ${integer(k.movedPipelineCount)} deal(s) have moved between pipelines — their full journey is counted across both.`}
            />
          )}
        </Box>
      )}

      {error && (
        <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 2, mb: 2, color: 'error.main', fontSize: 13 }}>
          Failed to load — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Paper>
      )}

      {/* ── KPI rail ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 2, mb: 2.5 }}>
        <Kpi label="Deals Created" icon={<LayersIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.indigo}
          value={k ? integer(k.dealsCreated) : '—'}
          definition="Marketing-sourced deals created in the selected window, across all pipelines. This is the funnel's entry cohort." />
        <Kpi label="Est. MBR Created" icon={<CurrencyRupeeIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.purple}
          value={k ? compactCurrency(k.estMbrCreated) : '—'}
          definition={`Σ Est. MBR of the created cohort — the monthly pipeline value generated in the window. ${MBR_TITLE}`} />
        <Kpi label="Won" icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.teal}
          value={k ? integer(k.won) : '—'} sub={k ? compactCurrency(k.wonValue) : undefined}
          definition="Deals from the created cohort now at Agreement Signed or Onboarding Completed." />
        <Kpi label="Lost" icon={<TrendingDownIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.pink}
          value={k ? integer(k.lost) : '—'} sub={k ? compactCurrency(k.lostValue) : undefined}
          definition="Deals closed-lost in the window, by close date — each attributed to the last live stage it actually occupied (see Where the pipeline leaks, below)." />
        <Kpi label="End-to-End Conversion" icon={<PercentIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.amber}
          value={k ? percentage(k.endToEndConversionPct) : '—'}
          definition="Deals from the created cohort that reached Onboarding Completed ÷ deals that reached RFI Sent." />
        <Kpi label="Open Backlog" icon={<Inventory2Icon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.sky}
          value={k ? integer(k.openBacklogCount) : '—'} sub={k ? compactCurrency(k.openBacklogValue) : undefined}
          definition="Every currently-open marketing-sourced deal across all pipelines — a LIVE snapshot, independent of the period filter. A workload gauge, not a window metric." />
      </Box>

      {/* ── Numbered lead→deal→stage→outcome flow ────────────────────────── */}
      {data && data.leadFlow && <PipelineLeadFlow data={data} />}

      {/* ── The ladder ───────────────────────────────────────────────────── */}
      <PfSection
        title="Canonical Stage Ladder"
        description="Cumulative — a deal counted at Commercial Negotiation genuinely passed every rung before it. Click a rung to break it down by pipeline, source and owner."
        right={data && <ScopeTag label={`Cohort: ${data.window.label}`} title="Deals created in the selected period." />}
      >
        {!data && isFetching && <Typography sx={{ p: 4, textAlign: 'center', fontSize: 13, color: GX_INK_500 }}>Loading…</Typography>}
        {data && data.steps && (
          <>
            <TaperedFunnel stages={funnelStages} minWidthPct={38} activeKey={selectedStep == null ? null : String(selectedStep)}
              onStageClick={(key) => toggleStep(Number(key))} />
            <Box sx={{ mt: 2 }}>
              <DataTable
                rows={data.steps} paginate={false} dense rowKey={(s) => String(s.step)}
                onRowClick={(s) => toggleStep(s.step)} isRowActive={(s) => s.step === selectedStep}
                columns={[
                  { key: 'label', header: 'Canonical Stage', title: 'The cross-pipeline rung. Pipeline-specific stages are folded in: Tech Demo / Solution Design / As is Study Done → Solutioning; Prozo Warehouse Visit → Commercial Negotiation.', cell: (s) => <Box component="span" sx={{ fontWeight: 600 }}>{s.label}</Box>, sortValue: (s) => s.step },
                  { key: 'reached', header: 'Ever Reached', align: 'right', title: 'Deals from the created cohort whose stage history shows they reached at least this rung. Cumulative.', cell: (s) => integer(s.deals), sortValue: (s) => s.deals },
                  { key: 'conv', header: 'Conv. from Prev', align: 'right', title: 'Deals reaching this rung ÷ deals reaching the previous rung. Blank on the first rung — there is no prior stage. Only computable because stage-entry timestamps are captured.', cell: (s) => (s.conversionFromPrevPct == null ? '—' : percentage(s.conversionFromPrevPct)), sortValue: (s) => s.conversionFromPrevPct },
                  { key: 'drop', header: 'Drop-off', align: 'right', title: 'How many deals reached the previous rung but never this one.', cell: (s) => (s.dropOffFromPrev == null ? '—' : integer(s.dropOffFromPrev)), sortValue: (s) => s.dropOffFromPrev },
                  { key: 'pctEntry', header: '% of Entry', align: 'right', title: 'Deals reaching this rung as a share of the first rung — the funnel taper width basis.', cell: (s) => percentage(s.pctOfEntryPct), sortValue: (s) => s.pctOfEntryPct },
                  { key: 'openHere', header: 'Sitting Here', align: 'right', title: 'Deals from the cohort that are still OPEN and currently on this exact rung — distinct from the cumulative Ever Reached.', cell: (s) => integer(s.openHere), sortValue: (s) => s.openHere },
                  { key: 'lostHere', header: 'Lost From Here', align: 'right', title: 'Deals closed-lost in the window whose last live rung was this one. This is where the pipeline actually leaks.', cell: (s) => (s.lostFromHere ? <Neg>{integer(s.lostFromHere)}</Neg> : '—'), sortValue: (s) => s.lostFromHere },
                  { key: 'mbr', header: 'Est. MBR', align: 'right', title: `Σ Est. MBR of the deals that reached this rung. ${MBR_TITLE}`, cell: (s) => compactCurrency(s.estimatedMbr), sortValue: (s) => s.estimatedMbr },
                ]}
              />
            </Box>
          </>
        )}
      </PfSection>

      {/* ── Selected-rung drill: pipeline → source → owner → deals ───────── */}
      {activeStep && activeStepMeta && (
        <PfSection
          title={`${activeStep.label} · breakdown`}
          description={`${integer(activeStepMeta.deals)} deals ever reached this rung. Expand a pipeline for its sources, then an owner for their deals.`}
          right={<Button size="small" onClick={() => setSelectedStep(null)} sx={{ textTransform: 'none', fontSize: 13 }}>Clear</Button>}
        >
          <ExpandableTable
            rows={activeStep.pipelines} paginate={false} rowKey={(p) => p.pipelineLabel}
            columns={[
              { key: 'pipeline', header: 'Pipeline', cell: (p) => <Box component="span" sx={{ fontWeight: 600 }}>{p.pipelineLabel}</Box>, sortValue: (p) => p.pipelineLabel },
              { key: 'deals', header: 'Deals', align: 'right', cell: (p) => integer(p.deals), sortValue: (p) => p.deals },
              { key: 'mbr', header: 'Est. MBR', align: 'right', title: MBR_TITLE, cell: (p) => compactCurrency(p.estimatedMbr), sortValue: (p) => p.estimatedMbr },
              { key: 'sources', header: 'Sources', align: 'right', title: 'Distinct Deal Source Sub Categories feeding this pipeline at this rung. Leads have no pipeline, so source is read off the DEAL.', cell: (p) => integer(p.sources.length), sortValue: (p) => p.sources.length },
            ]}
            renderExpanded={(p) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
                {p.sources.map((s) => (
                  <Box key={s.subcategory} sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '10px', p: 1.5, bgcolor: '#fff' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: GX_INK_900 }}>{s.subcategory}</Typography>
                      <Badge>{integer(s.deals)} deals</Badge>
                      <Badge title={MBR_TITLE}>{compactCurrency(s.estimatedMbr)}</Badge>
                    </Box>
                    <ExpandableTable
                      rows={s.owners} paginate={false} dense rowKey={(o) => o.owner}
                      columns={[
                        { key: 'owner', header: 'Sales POC', title: 'Who the deal is assigned to in HubSpot — "who got it".', cell: (o) => <Box component="span" sx={{ fontWeight: 500 }}>{o.owner}</Box>, sortValue: (o) => o.owner },
                        { key: 'team', header: 'Team', cell: (o) => o.team || '—', sortValue: (o) => o.team || '' },
                        { key: 'deals', header: 'Deals', align: 'right', cell: (o) => integer(o.deals), sortValue: (o) => o.deals },
                        { key: 'open', header: 'Open', align: 'right', cell: (o) => integer(o.open), sortValue: (o) => o.open },
                        { key: 'won', header: 'Won', align: 'right', cell: (o) => integer(o.won), sortValue: (o) => o.won },
                        { key: 'lost', header: 'Lost', align: 'right', cell: (o) => (o.lost ? <Neg>{integer(o.lost)}</Neg> : '—'), sortValue: (o) => o.lost },
                        { key: 'mbr', header: 'Est. MBR', align: 'right', title: MBR_TITLE, cell: (o) => compactCurrency(o.estimatedMbr), sortValue: (o) => o.estimatedMbr },
                      ]}
                      renderExpanded={(o) => (
                        <Box sx={{ p: 1 }}>
                          <DealTable hub={hub}
                            rows={activeStepDeals.filter((d) => d.pipelineLabel === p.pipelineLabel && d.subcategory === s.subcategory && d.owner === o.owner)} />
                        </Box>
                      )}
                    />
                  </Box>
                ))}
              </Box>
            )}
          />
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: GX_INK_900 }}>All {integer(activeStepDeals.length)} deals at this rung</Typography>
            <DealTable rows={activeStepDeals} hub={hub} />
          </Box>
        </PfSection>
      )}

      {/* ── Pipeline × stage matrix (live backlog) ───────────────────────── */}
      {data && data.matrix && (
        <PfSection
          title="Pipeline × Stage — where the open work sits"
          description="Currently-open deals by pipeline and rung. A LIVE snapshot — independent of the period filter."
          right={(
            <>
              <ScopeTag label="Live snapshot" title="Not windowed. This is the current backlog, so it answers 'where is my work right now' rather than 'what did this period generate'." />
              <Box sx={{ display: 'flex', border: `1px solid ${GX_BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
                <ToggleButton active={matrixMode === 'deals'} onClick={() => setMatrixMode('deals')}>Deals</ToggleButton>
                <ToggleButton active={matrixMode === 'mbr'} onClick={() => setMatrixMode('mbr')}>Est. MBR</ToggleButton>
              </Box>
            </>
          )}
        >
          <DataTable
            rows={data.matrix} paginate={false} rowKey={(m) => m.pipelineId}
            columns={[
              { key: 'pipeline', header: 'Pipeline', cell: (m) => <Box component="span" sx={{ fontWeight: 600 }}>{m.pipelineLabel}</Box>, sortValue: (m) => m.pipelineLabel },
              ...ladder.map((l) => ({
                key: `s${l.step}`, header: l.label, align: 'right',
                title: `Open deals currently sitting on "${l.label}" in this pipeline.`,
                cell: (m) => {
                  const c = m.cells ? m.cells[String(l.step)] : null
                  const v = c ? (matrixMode === 'deals' ? c.deals : c.estimatedMbr) : 0
                  if (!v) return <Muted />
                  return matrixMode === 'deals' ? integer(v) : compactCurrency(v)
                },
                sortValue: (m) => {
                  const c = m.cells ? m.cells[String(l.step)] : null
                  return c ? (matrixMode === 'deals' ? c.deals : c.estimatedMbr) : 0
                },
              })),
              { key: 'open', header: 'Open Total', align: 'right', title: 'All currently-open deals in this pipeline. Includes any sitting on a rung with no captured history.', cell: (m) => <Box component="span" sx={{ fontWeight: 600 }}>{matrixMode === 'deals' ? integer(m.openDeals) : compactCurrency(m.openMbr)}</Box>, sortValue: (m) => (matrixMode === 'deals' ? m.openDeals : m.openMbr) },
              { key: 'winRate', header: 'Win %', align: 'right', title: 'Deals won ÷ ALL deals created, both within the selected window (WIP counts in the denominator). This column IS windowed, unlike the stage cells.', cell: (m) => percentage(m.winRatePct), sortValue: (m) => m.winRatePct },
              { key: 'closure', header: 'Closure %', align: 'right', title: 'Won ÷ (Won + Lost) in the window — decided deals only. Same definition as Closure % on the Marketing dashboard.', cell: (m) => percentage(m.closurePct), sortValue: (m) => m.closurePct },
            ]}
          />
        </PfSection>
      )}

      {/* ── Leakage ──────────────────────────────────────────────────────── */}
      {data && data.leakageByStep && data.leakageByStep.length > 0 && (
        <PfSection
          title="Where the pipeline leaks"
          icon={<OpacityIcon sx={{ fontSize: 18 }} />}
          description="Lost deals attributed to the last live rung they occupied — not to “Closed Lost”, which says nothing about where they died."
          right={<ScopeTag label={`Closed-lost: ${data.window.label}`} title="Deals closed-lost in the selected window, by close date." />}
        >
          <ExpandableTable
            rows={data.leakageByStep} paginate={false} rowKey={(l) => String(l.step)}
            columns={[
              { key: 'label', header: 'Lost From Stage', cell: (l) => <Box component="span" sx={{ fontWeight: 600 }}>{l.label}</Box>, sortValue: (l) => l.step },
              { key: 'deals', header: 'Deals Lost', align: 'right', cell: (l) => <Neg>{integer(l.deals)}</Neg>, sortValue: (l) => l.deals },
              { key: 'mbr', header: 'Est. MBR Lost', align: 'right', title: MBR_TITLE, cell: (l) => compactCurrency(l.estimatedMbr), sortValue: (l) => l.estimatedMbr },
              { key: 'sources', header: 'Top Sources', title: 'Which Deal Source Sub Categories lose most at this rung.', cell: (l) => l.topSources.map((s) => `${s.subcategory} (${s.deals})`).join(', ') || '—', cellSx: { whiteSpace: 'normal', minWidth: 220 } },
              { key: 'owners', header: 'Top POCs', title: 'Which Sales POCs lose most at this rung.', cell: (l) => l.topOwners.map((o) => `${o.owner} (${o.deals})`).join(', ') || '—', cellSx: { whiteSpace: 'normal', minWidth: 200 } },
            ]}
            renderExpanded={(l) => (
              <Box sx={{ p: 1.5 }}>
                <DataTable
                  rows={l.dealRows} dense initialPageSize={25} rowKey={(d) => d.dealId}
                  filter={(d, q) => `${d.dealId} ${d.dealName || ''} ${d.owner} ${d.subcategory} ${d.pipelineLabel}`.toLowerCase().includes(q)}
                  filterPlaceholder="Filter lost deals…"
                  columns={[
                    { key: 'deal', header: 'Deal ID', cell: (d) => d.dealId, sortValue: (d) => d.dealId },
                    { key: 'hubspot', header: 'HubSpot', title: 'Opens this deal in HubSpot.', cell: (d) => <HubspotLink hub={hub} dealId={d.dealId} />, sortValue: (d) => d.dealId },
                    { key: 'pipeline', header: 'Pipeline', cell: (d) => d.pipelineLabel, sortValue: (d) => d.pipelineLabel },
                    { key: 'lostFrom', header: 'Died At', title: 'The exact HubSpot stage it last occupied before being marked lost.', cell: (d) => d.lostFromStageLabel || '—', sortValue: (d) => d.lostFromStageLabel || '' },
                    { key: 'movedFrom', header: 'Stage Pipeline', title: 'The pipeline that stage belongs to. Differs from the deal’s current pipeline when the deal was moved between pipelines before it died.', cell: (d) => (d.lostFromPipelineLabel && d.lostFromPipelineLabel !== d.pipelineLabel ? <Badge title="This deal was moved between pipelines">{d.lostFromPipelineLabel}</Badge> : '—'), sortValue: (d) => d.lostFromPipelineLabel || '' },
                    { key: 'source', header: 'Source', cell: (d) => d.subcategory, sortValue: (d) => d.subcategory },
                    { key: 'owner', header: 'Sales POC', cell: (d) => d.owner, sortValue: (d) => d.owner },
                    { key: 'mbr', header: 'Est. MBR', align: 'right', title: MBR_TITLE, cell: (d) => compactCurrency(d.estimatedMbr), sortValue: (d) => d.estimatedMbr },
                    { key: 'closed', header: 'Closed', cell: (d) => d.closedDate || '—', sortValue: (d) => d.closedDate || '' },
                  ]}
                />
              </Box>
            )}
          />
        </PfSection>
      )}

      {/* ── Stalled open deals ───────────────────────────────────────────── */}
      {data && data.stalled && data.stalled.length > 0 && (
        <PfSection
          title="Stalled open deals"
          icon={<HourglassBottomIcon sx={{ fontSize: 18 }} />}
          description={`${integer(data.stalled.length)} open deals have sat on the same rung for 30+ days. Measurable only because stage-entry timestamps are captured.`}
          right={<ScopeTag label="Live snapshot" title="Not windowed — the current backlog, ordered by longest time on the current stage." />}
        >
          <DealTable rows={data.stalled} hub={hub} showDays />
        </PfSection>
      )}
    </Box>
  )
}
